import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { parseApiError } from '@saas-kit/lib';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  toast,
} from '@saas-kit/ui';

import { revokeApiKey, type ApiKeyRecord } from './api';

interface Props {
  keys: ApiKeyRecord[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

export function ApiKeyList({
  keys,
  isLoading,
  isError,
  error,
}: Props): JSX.Element {
  const { t } = useTranslation('apiKeys');
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<ApiKeyRecord | null>(null);

  const mutation = useMutation({
    mutationFn: revokeApiKey,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tenant-api-keys'] });
      setPending(null);
      toast.success(t('toasts.revoked'));
    },
    onError: (e) => toast.error(t(parseApiError(e).i18nKey)),
  });

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('list.title')}</CardTitle>
          <CardDescription>{t('list.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          {isError ? (
            <ErrorBanner>{t(parseApiError(error).i18nKey)}</ErrorBanner>
          ) : keys.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-border text-start text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('list.columns.name')}</th>
                    <th className="px-3 py-2 font-medium">{t('list.columns.prefix')}</th>
                    <th className="px-3 py-2 font-medium">{t('list.columns.scopes')}</th>
                    <th className="px-3 py-2 font-medium">{t('list.columns.last_used')}</th>
                    <th className="px-3 py-2 font-medium">{t('list.columns.status')}</th>
                    <th className="px-3 py-2 font-medium">{t('list.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((apiKey) => (
                    <ApiKeyRow
                      key={apiKey.id}
                      apiKey={apiKey}
                      onRevoke={() => setPending(apiKey)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : isLoading ? (
            <p className="text-sm text-muted-foreground">{t('loading')}</p>
          ) : (
            <EmptyState
              title={t('list.empty_title')}
              description={t('list.empty')}
            />
          )}
        </CardContent>
      </Card>
      {pending ? (
        <ConfirmDialog
          title={t('confirm.revoke.title', { name: pending.name })}
          body={t('confirm.revoke.body', { name: pending.name })}
          confirmLabel={t('actions.revoke')}
          cancelLabel={t('cancel')}
          variant="destructive"
          isBusy={mutation.isPending}
          onCancel={() => setPending(null)}
          onConfirm={() => mutation.mutate(pending.id)}
        />
      ) : null}
    </>
  );
}

function ApiKeyRow({
  apiKey,
  onRevoke,
}: {
  apiKey: ApiKeyRecord;
  onRevoke: () => void;
}): JSX.Element {
  const { t } = useTranslation('apiKeys');
  return (
    <tr className="border-b border-border/70">
      <td className="px-3 py-3">{apiKey.name}</td>
      <td className="px-3 py-3 font-mono text-xs">{apiKey.prefix}</td>
      <td className="px-3 py-3">
        <div className="flex flex-wrap gap-1.5">
          {apiKey.scopes.length > 0 ? (
            apiKey.scopes.map((scope) => (
              <Badge key={scope} variant="outline">
                {scope}
              </Badge>
            ))
          ) : (
            <span className="text-muted-foreground">{t('list.no_scopes')}</span>
          )}
        </div>
      </td>
      <td className="px-3 py-3">
        {apiKey.last_used_at ? formatDate(apiKey.last_used_at) : '—'}
      </td>
      <td className="px-3 py-3">
        <Badge variant={apiKey.revoked_at ? 'secondary' : 'outline'}>
          {apiKey.revoked_at ? t('status.revoked') : t('status.active')}
        </Badge>
      </td>
      <td className="px-3 py-3">
        {apiKey.revoked_at ? (
          <span className="text-xs text-muted-foreground">
            {t('list.revoked_at', { date: formatDate(apiKey.revoked_at) })}
          </span>
        ) : (
          <Button size="sm" variant="destructive" onClick={onRevoke}>
            {t('actions.revoke')}
          </Button>
        )}
      </td>
    </tr>
  );
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString();
}
